import { ASSIGNED_SELECT_NONE } from "@sd/lib/adminTicketUtils";
import {
  emptyCreatedDateFilter,
  getDefaultCreatedDateRange,
  hasCreatedDateFilter,
  normalizeCreatedDateFilter,
  type CreatedDateFilterState,
} from "@sd/lib/ticketCreatedDateFilter";
import {
  formatStatusDashboardProjectParam,
  parseStatusDashboardProjectParam,
} from "@sd/lib/statusDashboardApi";
import { normalizeMultiFilterValues } from "@sd/lib/ticketFilterApi";

export interface TrackerReportFilters {
  projectFilters: string[];
  assigneeFilter: string;
  createdDateFilter: CreatedDateFilterState;
}

export function defaultTrackerReportFilters(): TrackerReportFilters {
  return {
    projectFilters: [],
    assigneeFilter: "all",
    createdDateFilter: getDefaultCreatedDateRange(),
  };
}

export function emptyTrackerReportFilters(): TrackerReportFilters {
  return {
    projectFilters: [],
    assigneeFilter: "all",
    createdDateFilter: emptyCreatedDateFilter(),
  };
}

export function hasActiveTrackerReportFilters(
  filters: TrackerReportFilters
): boolean {
  return (
    normalizeMultiFilterValues(filters.projectFilters).length > 0 ||
    (filters.assigneeFilter.trim() && filters.assigneeFilter !== "all") ||
    hasCreatedDateFilter(filters.createdDateFilter)
  );
}

export function buildTrackerReportApiParams(
  filters: TrackerReportFilters
): Record<string, string | boolean> {
  const params: Record<string, string | boolean> = {};
  const project = formatStatusDashboardProjectParam(filters.projectFilters);
  if (project) {
    params.project = project;
  }

  const dates = normalizeCreatedDateFilter(filters.createdDateFilter);
  if (dates.from) params.start_date = dates.from;
  if (dates.to) params.end_date = dates.to;

  const assignee = filters.assigneeFilter.trim();
  if (assignee && assignee !== "all") {
    if (assignee === ASSIGNED_SELECT_NONE) {
      params.assigned_to__isnull = true;
    } else {
      params.assigned_to = assignee;
    }
  }

  return params;
}

export function appendTrackerReportFilterParams(
  params: URLSearchParams,
  filters: TrackerReportFilters
): void {
  const project = formatStatusDashboardProjectParam(filters.projectFilters);
  if (project) params.set("project", project);

  const dates = normalizeCreatedDateFilter(filters.createdDateFilter);
  if (dates.from) params.set("start_date", dates.from);
  if (dates.to) params.set("end_date", dates.to);

  const assignee = filters.assigneeFilter.trim();
  if (assignee && assignee !== "all") {
    if (assignee === ASSIGNED_SELECT_NONE) {
      params.set("assigned_to__isnull", "true");
    } else {
      params.set("assigned_to", assignee);
    }
  }
}

export function parseAssigneeFilterFromSearchParams(
  searchParams: URLSearchParams
): string {
  if (searchParams.get("assigned_to__isnull") === "true") {
    return ASSIGNED_SELECT_NONE;
  }
  const assignedTo = searchParams.get("assigned_to")?.trim();
  return assignedTo || "all";
}

export function parseTrackerReportFiltersFromSearchParams(
  searchParams: URLSearchParams
): TrackerReportFilters | null {
  const project = searchParams.get("project")?.trim();
  const startDate = searchParams.get("start_date")?.trim();
  const endDate = searchParams.get("end_date")?.trim();
  const assigneeFilter = parseAssigneeFilterFromSearchParams(searchParams);
  const projectFilters = project ? parseStatusDashboardProjectParam(project) : [];

  if (
    projectFilters.length === 0 &&
    !startDate &&
    !endDate &&
    assigneeFilter === "all"
  ) {
    return null;
  }

  return {
    projectFilters,
    assigneeFilter,
    createdDateFilter: normalizeCreatedDateFilter({
      from: startDate ?? "",
      to: endDate ?? "",
    }),
  };
}

export function mergeTrackerReportFilters(
  partial: Partial<TrackerReportFilters> | null | undefined
): TrackerReportFilters {
  const defaults = defaultTrackerReportFilters();
  if (!partial) return defaults;
  return {
    projectFilters:
      partial.projectFilters !== undefined
        ? normalizeMultiFilterValues(partial.projectFilters)
        : defaults.projectFilters,
    assigneeFilter: partial.assigneeFilter?.trim() || defaults.assigneeFilter,
    createdDateFilter: normalizeCreatedDateFilter(
      partial.createdDateFilter ?? defaults.createdDateFilter
    ),
  };
}
